import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useMask } from "@react-input/mask";
import { useMsal } from "@azure/msal-react";

import { SideBar } from "../components/SideBar";
import { FooterDashboard } from "../components/FooterDashboard";

import AuthenticatedTemplate from "../components/AuthenticatedTemplate";
import Header from "../components/Header";
import Breadcrumbs from "../components/Breadcrumbs";
import Button from "../components/form/Button";
import { PopUp } from "../components/PopUp";
import { useGlobalStore } from "../stores/global-store";

export const DashboardProfile = () => {
  const { instance } = useMsal();
  let activeAccount = instance.getActiveAccount();
  const [showPopUp, setShowPopUp] = useState(false);
  const [popUpText, setPopUpText] = useState("");
  const [breadcrumbs] = useState([
    {
      name: "Dashboard",
      path: "/dashboard-return",
    },
    {
      name: "Profile",
    },
  ]);
  const { gender, fetchGender, maritalStatus, fetchMaritalStatus } =
    useGlobalStore();
  const [updateData, setUpdateData] = useState({
    email: "",
    gender: null,
    maritalStatus: null,
    dob: "",
  });
  const [loadingUpdate, setLoadingUpdate] = useState(false);
  const [personalDetails, setPersonalDetails] = useState({
    Customer_Type: 0,
    Customer_ID: 0,
    Personal_Details_ID: 0,
    Given_Name: "",
    Middle_Name: "",
    Surname: "",
    Gender: 0,
    Marital_Status: null,
    Date_Of_Birth: null,
    Id_Type: 1,
    Id_Value: "",
    Id_Value_Extra: "",
    Id_Expiry_Date: null,
    Email: "",
    Mobile_Number: "",
    Res_Phone_Number: "",
    Work_Phone_Number: "",
    Res_Address_Building_Name: "",
    Res_Address_Unit_Number: "",
    Res_Address_Street_Number: "",
    Res_Address_Street_Name: "",
    Res_Address_Suburb: "",
    Res_Address_City: "",
    Res_Address_State_Region: "",
    Res_Address_Country: "",
    Res_Address_Post_Code: "",
    Post_Address_Same: true,
    Post_Address_Building_Name: "",
    Post_PO_Box: "",
    Post_Address_Unit_Number: "",
    Post_Address_Street_Number: "",
    Post_Address_Street_Name: "",
    Post_Address_Suburb: "",
    Post_Address_City: "",
    Post_Address_State_Region: "",
    Post_Address_Country: "",
    Post_Address_Post_Code: "",
  });

  useEffect(() => {
    const initialize = async () => {
      try {
        await fetchMaritalStatus();
        await fetchGender();
        await fetchDataPersonalDetails();
      } catch (error) {
        console.error("Error initializing data:", error);
      }
    };
    initialize();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUpdateData((prev) => ({ ...prev, [name]: value || null }));
  };

  const updateCustomerDetails = async () => {
    const { email, gender, maritalStatus, dob } = updateData;
    const customerId = personalDetails.Customer_ID;

    if (!customerId) {
      console.log("Customer_ID is required to update details.");
      return;
    }

    setLoadingUpdate(true);

    try {
      const response = await axios.put(
        `${process.env.REACT_APP_URL}/user/update-details`,
        {
          email,
          gender,
          maritalStatus,
          dob,
          customerId,
        }
      );
      if (response?.data) {
        const { affectedRows } = response.data;
        //If the Personal Details was updated, the affectedRows will equals to one
        if (affectedRows === 1) {
          setShowPopUp(true);
          setPopUpText("Your profile has been edited.");
        }
      }
    } catch (error) {
      setShowPopUp(true);
      setPopUpText("Failed to edit customer details.");
      console.error(
        "Error updating customer details:",
        error.response?.data || error.message
      );
    } finally {
      setLoadingUpdate(false);
    }
  };

  /**
   * Function to check if the email retrieved from the Token Claim is registred in the Database
   */
  async function fetchDataPersonalDetails() {
    let email = "";
    const LS_CUSTOMER_INFO = sessionStorage.getItem("LS_CUSTOMER_INFO");
    if (LS_CUSTOMER_INFO) {
      email = JSON.parse(LS_CUSTOMER_INFO).email;
    }
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_URL}/user/get-email-personal-details`,
        {
          email,
        }
      );

      if (res.data?.length > 0) {
        const personalDetails = res.data[0];
        setPersonalDetails(personalDetails);

        // Save to local storage
        sessionStorage.setItem(
          "LS_PERSONAL_DETAILS",
          JSON.stringify(personalDetails)
        );

        // Destructure and map values to the updateData state
        const {
          Email = "",
          Gender = null,
          Marital_Status = null,
          Date_Of_Birth = "",
        } = personalDetails;

        setUpdateData({
          email: Email,
          gender: Gender,
          maritalStatus: Marital_Status,
          dob: Date_Of_Birth,
        });
      }
    } catch (error) {
      console.error(
        "Error fetching personal details:",
        error?.response?.data || error.message
      );
    }
  }

  /**
   * Function to set a Mask to the date of birth
   */
  const inputRefDOBdate = useMask({
    mask: "__/__/____",
    replacement: { _: /\d/ },
  });

  return (
    <>
      <AuthenticatedTemplate>
        <PopUp
          open={showPopUp}
          title="Edit profile"
          text={popUpText}
          onClose={() => setShowPopUp(false)}
        />

        <SideBar />

        <Header title={"Profile"} breadcrumbs={breadcrumbs} />

        <main className="dashboard">
          <div className="topic-title">
            <h1 className="text-sm text-neutral-950">Profile</h1>
            <Breadcrumbs items={breadcrumbs} />
          </div>

          <div className="dashboard-profile">
            <section className="dashboard-profile__form">
              <article className="dashboard-profile__card">
                <h2 className="paragraph-lg semibold text-neutral-950">
                  Information
                </h2>

                <div className="form">
                  <div className="form__controller">
                    <label>Customer Type</label>
                    <input
                      name="customerType"
                      type="text"
                      readOnly
                      value={"Individual"}
                    />
                  </div>
                  <div className="form__controller">
                    <label>Given Name</label>
                    <input
                      name="givenName"
                      type="text"
                      readOnly
                      value={personalDetails?.Given_Name}
                    />
                  </div>
                  <div className="form__controller">
                    <label>Middle Name</label>
                    <input
                      name="middleName"
                      type="text"
                      readOnly
                      value={personalDetails?.Middle_Name}
                    />
                  </div>
                  <div className="form__controller">
                    <label>Surname</label>
                    <input
                      name="surname"
                      type="text"
                      readOnly
                      value={personalDetails?.Surname}
                    />
                  </div>
                  <div className="form__controller">
                    <label>Gender</label>
                    <select name="gender" onChange={handleChange}>
                      {personalDetails?.Gender ? (
                        <option value="">
                          {
                            gender[personalDetails.Gender - 1]
                              .Gender_Description
                          }
                        </option>
                      ) : (
                        <option value="">Select here</option>
                      )}
                      {gender
                        ? gender.map((item, index) => {
                            return (
                              <option key={index + 100} value={item.Gender_ID}>
                                {item.Gender_Description}
                              </option>
                            );
                          })
                        : null}
                    </select>
                  </div>
                  <div className="form__controller">
                    <label>Marital Status</label>
                    <select name="maritalStatus" onChange={handleChange}>
                      {personalDetails?.Marital_Status ? (
                        <option value="">
                          {
                            maritalStatus[personalDetails.Marital_Status - 1]
                              .Marital_Status_Description
                          }
                        </option>
                      ) : (
                        <option value="">Select here</option>
                      )}
                      {maritalStatus
                        ? maritalStatus.map((item, index) => {
                            return (
                              <option
                                key={index + 200}
                                value={item.Marital_Status_ID}
                              >
                                {item.Marital_Status_Description}
                              </option>
                            );
                          })
                        : null}
                    </select>
                  </div>
                  <div className="form__controller">
                    <label>Date of Birth</label>
                    <input
                      className="date-of-birth-input"
                      ref={inputRefDOBdate}
                      placeholder={
                        personalDetails?.Date_Of_Birth
                          ? personalDetails.Date_Of_Birth
                          : "dd / mm / yyyy"
                      }
                      name="dob"
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </article>

              <article className="dashboard-profile__card">
                <h2 className="paragraph-lg semibold text-neutral-950">
                  Contact
                </h2>

                <div className="form">
                  <div className="form__controller">
                    <label>Email</label>
                    <input
                      name="email"
                      type="email"
                      value={
                        personalDetails.Email
                          ? personalDetails.Email
                          : activeAccount?.username
                      }
                      onChange={handleChange}
                      readOnly
                    />
                  </div>
                  <div className="form__controller">
                    <label>Mobile Number</label>
                    <input
                      name="mobileNumber"
                      type="text"
                      readOnly
                      value={personalDetails?.Mobile_Number}
                    />
                  </div>
                </div>
              </article>

              <article className="dashboard-profile__card">
                <h2 className="paragraph-lg semibold text-neutral-950">
                  Residential Address
                </h2>

                <div className="form">
                  <div className="form__controller">
                    <label>Building Name</label>
                    <input
                      name="buildingName"
                      type="text"
                      readOnly
                      value={personalDetails?.Res_Address_Building_Name}
                    />
                  </div>
                  <div className="form__controller">
                    <label>Unit Number</label>
                    <input
                      name="unitNumber"
                      type="text"
                      readOnly
                      value={personalDetails?.Res_Address_Unit_Number}
                    />
                  </div>
                  <div className="form__controller">
                    <label>Street Number</label>
                    <input
                      name="streetNumber"
                      type="text"
                      readOnly
                      value={personalDetails?.Res_Address_Street_Number}
                    />
                  </div>
                  <div className="form__controller">
                    <label>Street Name</label>
                    <input
                      name="streetName"
                      type="text"
                      readOnly
                      value={personalDetails?.Res_Address_Street_Name}
                    />
                  </div>
                  <div className="form__controller">
                    <label>Suburb</label>
                    <input
                      name="suburb"
                      type="text"
                      readOnly
                      value={personalDetails?.Res_Address_Suburb}
                    />
                  </div>
                  <div className="form__controller">
                    <label>City</label>
                    <input
                      name="city"
                      type="text"
                      readOnly
                      value={personalDetails?.Res_Address_City}
                    />
                  </div>
                  <div className="form__controller">
                    <label>State/Region</label>
                    <input
                      name="stateRegion"
                      type="text"
                      readOnly
                      value={personalDetails?.Res_Address_State_Region}
                    />
                  </div>
                  <div className="form__controller">
                    <label>Country</label>
                    <input
                      name="country"
                      type="text"
                      readOnly
                      value={personalDetails?.Res_Address_Country}
                    />
                  </div>
                  <div className="form__controller">
                    <label>Post Code</label>
                    <input
                      name="postCode"
                      type="text"
                      readOnly
                      value={personalDetails?.Res_Address_Post_Code}
                    />
                  </div>
                </div>
              </article>
            </section>

            <div className="action-buttons">
              <Link to="/dashboard-return">
                <Button variant="outlined">Cancel</Button>
              </Link>
              <Button
                variant="primary"
                loading={loadingUpdate}
                onClick={() => updateCustomerDetails()}
              >
                Save
              </Button>
            </div>
          </div>
        </main>
        <FooterDashboard />
      </AuthenticatedTemplate>
    </>
  );
};
